import React, {useEffect} from 'react';
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import {styled} from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import CheckIcon from '@mui/icons-material/Check';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import {SampleFilesTable} from './SampleFilesTable';

// Copied from the same document in step3. Wanted to make more changes that
// seemed detrimental to keep in one file. Once done with changes, that choice
// can still be reevaluated.

// TODO Style header similar to Step3HasSamples.tsx?
const HeaderTableCell = styled(TableCell)(({theme}) => ({
    backgroundColor: theme.palette.surfaceContainerHigh,
    color: theme.palette.onSurface,
    fontWeight: 'bold'
}));

interface ChipSamplesTableProps {
    samples: any[];
    experiment: any;
    /** Map of control names to their sample objects */
    controls?: Record<string, any>;
    /** Set of sample IDs that have errors */
    errorIds?: Set<string>;
    /** Called after a file is uploaded or its metadata is edited */
    onChangeCompleted?: () => void;
    /** Option to hide sampleType column */
    sampleTypeColumn?: boolean;
    expectedFilenamesBySampleId?: Record<string, string[]>;
}

/**
 * Single table of ChIP samples.
 */
export const ChipSamplesTable = ({
    samples,
    experiment,
    controls,
    errorIds,
    onChangeCompleted,
    sampleTypeColumn = true,
    expectedFilenamesBySampleId
}: ChipSamplesTableProps) => {
    const [openIds, setOpenIds] = React.useState<Set<string>>(errorIds || new Set());

    // ensures table is expanded for sample with errors in atac seq
    // atac renders this component before reFetchSamples unlike chip
    useEffect(() => {
        if (!errorIds?.size) {
            return;
        }
        setOpenIds(prev => {
            const next = new Set(prev);
            let changed = false;
            errorIds.forEach(id => {
                if (!next.has(id)) {
                    next.add(id);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [errorIds]);

    /** Add or remove the sample ID from the set of folded open IDs */
    const toggleOpen = (id: string) => {
        setOpenIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    if (samples.length === 0) {
        return null;
    }

    return (
        <TableContainer sx={{maxHeight: '25em'}}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <HeaderTableCell></HeaderTableCell>
                        <HeaderTableCell>Sample Name</HeaderTableCell>
                        <HeaderTableCell align="right">Replicate</HeaderTableCell>
                        <HeaderTableCell>Factor</HeaderTableCell>
                        <HeaderTableCell>Tissue</HeaderTableCell>
                        <HeaderTableCell>Condition</HeaderTableCell>
                        <HeaderTableCell>Treatment</HeaderTableCell>
                        {sampleTypeColumn ? <HeaderTableCell>SampleType</HeaderTableCell> : null}
                        {controls ? <HeaderTableCell>ControlName</HeaderTableCell> : null}
                        <HeaderTableCell>Files</HeaderTableCell>
                        {controls ? <HeaderTableCell>Control Files</HeaderTableCell> : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {samples.map(sample => (
                        <React.Fragment key={sample.name}>
                            <TableRow>
                                <TableCell sx={{textAlign: 'center'}}>
                                    <IconButton size="small" onClick={() => toggleOpen(sample.id)}>
                                        {errorIds?.has(sample.id) ? <ErrorOutline color="error" /> : <CheckIcon />}
                                    </IconButton>
                                </TableCell>
                                <TableCell>{sample.name}</TableCell>
                                <TableCell align="right">{sample.other_categories.Replicate}</TableCell>
                                <TableCell>{sample.other_categories.Factor}</TableCell>
                                <TableCell>{sample.other_categories.Tissue}</TableCell>
                                <TableCell>{sample.other_categories.Condition}</TableCell>
                                <TableCell>{sample.other_categories.Treatment}</TableCell>
                                {sampleTypeColumn ? <TableCell>{sample.other_categories.SampleType}</TableCell> : null}
                                {controls ? <TableCell>{sample.other_categories.ControlName}</TableCell> : null}
                                <TableCell>
                                    {sample.files.map((f: any) => (
                                        <div key={f.id}>{f.name};</div>
                                    ))}
                                </TableCell>
                                {controls ? (
                                    <TableCell>
                                        {controls[sample.other_categories.ControlName]?.files.map((f: any) => (
                                            <div key={f.id}>{f.name};</div>
                                        ))}
                                    </TableCell>
                                ) : null}
                            </TableRow>
                            {openIds?.has(sample.id) && (
                                <TableRow>
                                    <TableCell sx={{textAlign: 'right'}}>
                                        <SubdirectoryArrowRightIcon />
                                    </TableCell>
                                    <TableCell colSpan={7}>
                                        <SampleFilesTable
                                            sample={sample}
                                            experiment={experiment}
                                            onUploadSuccess={onChangeCompleted}
                                            onFileMetaEdited={onChangeCompleted}
                                            expectedFilenames={expectedFilenamesBySampleId?.[sample.id] || []}
                                        />
                                    </TableCell>
                                    <TableCell colSpan={controls ? 3 : 1}>{/* Ensures row border is drawn */}</TableCell>
                                </TableRow>
                            )}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
